// Pre-join choices: mic and camera on/off and which device, camera preview,
// headphones advice, and the shared-mic choice that the api stores as
// server-controlled participant metadata. Recording status here is only a
// hint from presence; the authoritative notice follows POST /token.
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { COPY } from '../copy.js';
import type { MediaDevicesPort } from '../services.js';
import { ErrorNote, Field } from '../ui/primitives.js';
import { DevicePreview } from './DevicePreview.js';

export interface JoinChoices {
  audio: boolean;
  video: boolean;
  audioDeviceId: string | null;
  videoDeviceId: string | null;
  sharedMic: boolean;
}

interface DeviceOption {
  id: string;
  label: string;
}

function useDevices(media: MediaDevicesPort | null, kind: MediaDeviceKind): DeviceOption[] {
  const [devices, setDevices] = useState<DeviceOption[]>([]);
  useEffect(() => {
    if (!media) return;
    let cancelled = false;
    media
      .enumerateDevices()
      .then((all) => {
        if (cancelled) return;
        const ofKind = all.filter((d) => d.kind === kind && d.deviceId);
        setDevices(ofKind.map((d, i) => ({ id: d.deviceId, label: d.label || `${kind === 'audioinput' ? COPY.microphone : COPY.camera} ${i + 1}` })));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [media, kind]);
  return devices;
}

export function PreJoin({
  slug,
  media,
  liveHint,
  busy,
  error,
  narrow,
  onJoin,
}: {
  slug: string;
  media: MediaDevicesPort | null;
  /** From presence: a meeting is live here. Never a recording claim. */
  liveHint: boolean;
  busy: boolean;
  error: string | null;
  narrow: boolean;
  onJoin: (choices: JoinChoices) => void;
}) {
  const [choices, setChoices] = useState<JoinChoices>({
    audio: true,
    video: true,
    audioDeviceId: null,
    videoDeviceId: null,
    sharedMic: false,
  });
  const mics = useDevices(media, 'audioinput');
  const cameras = useDevices(media, 'videoinput');
  const set = (patch: Partial<JoinChoices>) => setChoices((c) => ({ ...c, ...patch }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!busy) onJoin(choices);
  };

  return (
    <form className={narrow ? 'prejoin prejoin--narrow' : 'prejoin'} onSubmit={submit} data-testid="prejoin">
      <h1>{COPY.roomTitle(slug)}</h1>
      <div className="prejoin__grid">
        <DevicePreview media={media} enabled={choices.video} deviceId={choices.videoDeviceId} />
        <fieldset className="prejoin__controls">
          <label className="device-toggle">
            <input type="checkbox" checked={choices.audio} onChange={(e) => set({ audio: e.target.checked })} />{' '}
            {COPY.microphone}
          </label>
          {mics.length > 0 && (
            <Field label={COPY.micDevice}>
              <select value={choices.audioDeviceId ?? ''} onChange={(e) => set({ audioDeviceId: e.target.value || null })}>
                <option value="">{COPY.defaultDevice}</option>
                {mics.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <label className="device-toggle">
            <input type="checkbox" checked={choices.video} onChange={(e) => set({ video: e.target.checked })} />{' '}
            {COPY.camera}
          </label>
          {cameras.length > 0 && (
            <Field label={COPY.cameraDevice}>
              <select value={choices.videoDeviceId ?? ''} onChange={(e) => set({ videoDeviceId: e.target.value || null })}>
                <option value="">{COPY.defaultDevice}</option>
                {cameras.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <p className="advice">{COPY.headphones}</p>
          <label>
            <input
              type="checkbox"
              checked={choices.sharedMic}
              onChange={(e) => set({ sharedMic: e.target.checked })}
            />{' '}
            {COPY.sharedMic}
          </label>
          <p className="muted small">{COPY.sharedMicHelp}</p>
          {liveHint && <p className="muted">{COPY.liveRecordingHint}</p>}
          {error && (
            <ErrorNote message={error} />
          )}
          <button type="submit" className="button primary" disabled={busy}>
            {busy ? COPY.requestingToken : COPY.enterRoom}
          </button>
          <p className="muted small">{COPY.recordingDecidedOnEntry}</p>
        </fieldset>
      </div>
    </form>
  );
}
